export class ProductScrollContextInitializer {
	constructor() {
		const scrollytellingElements = document.querySelectorAll<HTMLVideoElement>(
			'.scrollytelling-module-container .scrollytelling-video',
		);
		scrollytellingElements.forEach((scrollytelling) => {
			new Scrollytelling(scrollytelling);
		});

		const stickyImages = document.querySelectorAll<HTMLImageElement>(
			'.product-scroll-container.image .image-container.header-image img',
		);

		stickyImages.forEach((image) => {
			new StickyMediaController(image);
		});
	}
}

class StickyMediaController {
	private image: HTMLImageElement;

	private container: HTMLDivElement;
	private contentContainer: HTMLDivElement;
	private productFeaturesContainer: HTMLDivElement;
	private galleryContainer: HTMLDivElement;

	constructor(image: HTMLImageElement) {
		this.image = image;

		this.container = this.image.closest<HTMLDivElement>('.product-scroll-container.image')!;
		this.contentContainer = this.container.querySelector<HTMLDivElement>('.scrollytelling-content-container')!;
		this.productFeaturesContainer =
			this.contentContainer.querySelector<HTMLDivElement>('.product-features-container')!;
		this.galleryContainer = this.contentContainer.querySelector<HTMLDivElement>('.gallery-wrapper')!;

		this.fadeOutHandler();
	}

	private fadeOutHandler() {
		const distanceFromTop = window.scrollY + this.container.getBoundingClientRect().top;
		const contentHeight = this.contentContainer.scrollHeight;
		const featuresHeight = this.productFeaturesContainer ? this.productFeaturesContainer.scrollHeight : 0;
		const galleryHeight = this.galleryContainer ? this.galleryContainer.scrollHeight : 0;
		const endScrollPosition = contentHeight - window.innerHeight - featuresHeight;

		const rawPercentScrolled = (window.scrollY - distanceFromTop) / endScrollPosition;

		// Calculate features height as a percentage of contentHeight
		const featuresHeightPercent = featuresHeight / contentHeight;
		const galleryHeightPercent = galleryHeight / contentHeight;

		// Define fade start and end points based on rawPercentScrolled
		let fadeStart = 1 + featuresHeightPercent;
		let fadeEnd = fadeStart + 2 * galleryHeightPercent;

		// Edge cases where Features or Gallery are not maintained
		if (galleryHeightPercent === 0 && featuresHeightPercent != 0) {
			fadeEnd = fadeStart + featuresHeightPercent * 3;
		} else if (galleryHeightPercent === 0 && featuresHeightPercent === 0) {
			// This situation should preferably not come up because the opacity would start while the video is still ongoing
			fadeStart = 0.2;
		}

		// Calculate fadeOutProgress from 0 to 1 between fadeStart and fadeEnd
		let fadeOutProgress = 0;

		if (rawPercentScrolled >= fadeStart) {
			fadeOutProgress = Math.min((rawPercentScrolled - fadeStart) / (fadeEnd - fadeStart), 1);
		} else {
			fadeOutProgress = 0;
		}

		this.image.style.opacity = String(1 - fadeOutProgress);

		setTimeout(() => {
			requestAnimationFrame(() => {
				// To keep the this-context
				this.fadeOutHandler();
			});
		}, 20);
	}
}

class Scrollytelling {
	private video: HTMLVideoElement;
	private container: HTMLDivElement;
	private contentContainer: HTMLDivElement;
	private productFeaturesContainer: HTMLDivElement;
	private galleryContainer: HTMLDivElement;
	private isSafari = false;

	constructor(video: HTMLVideoElement) {
		this.video = video;
		this.container = this.video.closest<HTMLDivElement>('.scrollytelling-module-container')!;
		this.contentContainer = this.container.querySelector<HTMLDivElement>('.scrollytelling-content-container')!;
		this.productFeaturesContainer =
			this.contentContainer.querySelector<HTMLDivElement>('.product-features-container')!;
		this.galleryContainer = this.contentContainer.querySelector<HTMLDivElement>('.gallery-wrapper')!;

		let userAgent = navigator.userAgent.toLowerCase();
		this.isSafari = userAgent.includes('safari') && !userAgent.includes('chrome');

		// Wait for the video data to load
		if (this.video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
			this.handleVideoReady();
		} else {
			this.video.addEventListener(
				'loadeddata',
				() => {
					this.handleVideoReady();
				},
				{ once: true },
			);
		}

		this.container
			.querySelectorAll<HTMLElement>('.accordion-item .accordion-item-header')
			.forEach((accordionTrigger) => {
				accordionTrigger.addEventListener('click', () => {
					const animationElement = accordionTrigger.nextElementSibling;
					if (!animationElement) return;

					// Get transition duration from CSS
					const computedStyle = window.getComputedStyle(animationElement);
					const transitionDuration = parseFloat(computedStyle.transitionDuration) * 1000; // Convert seconds to ms

					const startTime = performance.now();

					const updateHeight = () => {
						const elapsedTime = performance.now() - startTime;
						this.updateContainerHeight();

						// Continue updating while the transition is running
						if (elapsedTime < transitionDuration) {
							requestAnimationFrame(updateHeight);
						}
					};

					requestAnimationFrame(updateHeight);
				});
			});

		// Adjust height on resize
		window.addEventListener('resize', () => this.updateContainerHeight());
	}

	/** Public method to update the container height dynamically */
	public updateContainerHeight() {
		const contentHeight = this.contentContainer.scrollHeight;

		const videoContainer = this.video.parentElement as HTMLDivElement;
		videoContainer.style.height = `${contentHeight}px`;
	}

	private handleVideoReady() {
		this.updateContainerHeight();
		if (window.innerWidth > 980) {
			this.registerVideo();
		} else {
			this.video.autoplay = true;
			this.video.play();
		}
	}

	private registerVideo() {
		if (this.isSafari) {
			this.video.play();
		}

		const scrollVideo = () => {
			const distanceFromTop = window.scrollY + this.container.getBoundingClientRect().top;
			const contentHeight = this.contentContainer.scrollHeight;
			const featuresHeight = this.productFeaturesContainer ? this.productFeaturesContainer.scrollHeight : 0;
			const galleryHeight = this.galleryContainer ? this.galleryContainer.scrollHeight : 0;
			const endScrollPosition = contentHeight - window.innerHeight - featuresHeight;

			const rawPercentScrolled = (window.scrollY - distanceFromTop) / endScrollPosition;
			const percentScrolled = Math.min(Math.max(rawPercentScrolled, 0), 1);

			this.video.currentTime = this.video.duration * percentScrolled;

			// Keep last frame when video reaches 100%
			if (percentScrolled >= 1) {
				this.video.pause();
			}

			// Calculate features height as a percentage of contentHeight
			const featuresHeightPercent = featuresHeight / contentHeight;
			const galleryHeightPercent = galleryHeight / contentHeight;

			// Define fade start and end points based on rawPercentScrolled
			let fadeStart = 1 + featuresHeightPercent;
			let fadeEnd = fadeStart + 2 * galleryHeightPercent;

			// Edge cases where Features or Gallery are not maintained
			if (galleryHeightPercent === 0 && featuresHeightPercent != 0) {
				fadeEnd = fadeStart + featuresHeightPercent * 3;
			} else if (galleryHeightPercent === 0 && featuresHeightPercent === 0) {
				// This situation should preferably not come up because the opacity would start while the video is still ongoing
				fadeStart = 0.2;
			}

			// Calculate fadeOutProgress from 0 to 1 between fadeStart and fadeEnd
			let fadeOutProgress = 0;

			if (rawPercentScrolled >= fadeStart) {
				fadeOutProgress = Math.min((rawPercentScrolled - fadeStart) / (fadeEnd - fadeStart), 1);
			} else {
				fadeOutProgress = 0;
			}

			this.video.style.opacity = String(1 - fadeOutProgress);

			setTimeout(() => {
				requestAnimationFrame(scrollVideo);
			}, 20);
		};
		requestAnimationFrame(scrollVideo);
	}
}
